import { GAME_CONFIG, type GameConfig } from '../app/GameConfig';
import { GameState } from '../app/GameState';
import type { GameSettings, GameStateHydration, GameStateSnapshot } from '../app/types';
import { debugLogger } from './DebugLogger';

export type SaveSlotStatus = 'empty' | 'valid' | 'corrupt' | 'versionMismatch';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SaveSlotRecord {
  version: number;
  slotId: number;
  savedAt: string;
  title: string;
  snapshot: GameStateSnapshot;
}

export interface SaveSlotSummary {
  slotId: number;
  status: SaveSlotStatus;
  savedAt: string | null;
  title: string | null;
  reason?: string;
}

export interface SaveSlotLoadResult {
  slotId: number;
  status: SaveSlotStatus;
  record: SaveSlotRecord | null;
  reason?: string;
}

interface SaveEnvelope {
  version: number;
  slots: unknown[];
}

interface SettingsEnvelope {
  version: number;
  settings: GameSettings;
}

const SAVE_DATA_VERSION = 2;
const SAVE_SLOT_VERSION = 2;
const LEGACY_SAVE_DATA_VERSION = 1;
const LEGACY_SAVE_SLOT_VERSION = 1;
const SETTINGS_VERSION = 1;

export class SaveManager {
  private readonly slotCount: number;
  private readonly saveDataKey: string;
  private readonly settingsKey: string;
  private readonly storage: StorageLike;

  public constructor(
    private readonly config: GameConfig = GAME_CONFIG,
    storage: StorageLike = getDefaultStorage()
  ) {
    this.slotCount = this.config.saveSlotCount;
    this.saveDataKey = this.config.storageKeys.saveData;
    this.settingsKey = this.config.storageKeys.settings;
    this.storage = storage;
  }

  public listSlots(): SaveSlotSummary[] {
    const envelopeResult = this.readEnvelope();
    if (envelopeResult.status === 'corrupt') {
      debugLogger.log('save', 'listSlots corrupt envelope', {
        key: this.saveDataKey,
        reason: envelopeResult.reason
      });
      return this.createSlotRange().map((slotId) => ({
        slotId,
        status: 'corrupt',
        savedAt: null,
        title: null,
        reason: envelopeResult.reason
      }));
    }

    this.persistMigratedEnvelope(envelopeResult);
    const envelope = envelopeResult.envelope;
    const summaries = this.createSlotRange().map((slotId) =>
      summarizeSlot(slotId, envelope.slots[slotId - 1], envelopeResult.status, this.config)
    );
    debugLogger.log('save', 'listSlots', {
      key: this.saveDataKey,
      envelopeStatus: envelopeResult.status,
      slots: summaries.map((slot) => ({
        slotId: slot.slotId,
        status: slot.status,
        savedAt: slot.savedAt,
        title: slot.title
      }))
    });
    return summaries;
  }

  public hasAnySave(): boolean {
    return this.listSlots().some((slot) => slot.status === 'valid');
  }

  public saveSlot(slotId: number, snapshot: GameStateSnapshot, title: string): SaveSlotRecord {
    this.assertSlotId(slotId);
    const envelope = this.readEditableEnvelope();
    const record: SaveSlotRecord = {
      version: SAVE_SLOT_VERSION,
      slotId,
      savedAt: new Date().toISOString(),
      title,
      snapshot: normalizeSnapshot(snapshot, this.config)
    };

    envelope.slots[slotId - 1] = record;
    this.writeEnvelope(envelope);
    debugLogger.log('save', 'saveSlot', {
      key: this.saveDataKey,
      slotId,
      title,
      savedAt: record.savedAt,
      snapshot: summarizeSnapshot(record.snapshot)
    });
    return cloneRecord(record, this.config);
  }

  public loadSlot(slotId: number): SaveSlotLoadResult {
    this.assertSlotId(slotId);
    const envelopeResult = this.readEnvelope();
    if (envelopeResult.status === 'corrupt') {
      const result = {
        slotId,
        status: 'corrupt' as const,
        record: null,
        reason: envelopeResult.reason
      };
      debugLogger.log('save', 'loadSlot corrupt envelope', {
        key: this.saveDataKey,
        slotId,
        reason: envelopeResult.reason
      });
      return result;
    }

    this.persistMigratedEnvelope(envelopeResult);
    const slot = envelopeResult.envelope.slots[slotId - 1] ?? null;
    const result = parseSlot(slotId, slot, envelopeResult.status, this.config);
    debugLogger.log('save', 'loadSlot', {
      key: this.saveDataKey,
      slotId,
      status: result.status,
      reason: result.reason,
      savedAt: result.record?.savedAt ?? null,
      title: result.record?.title ?? null,
      snapshot: result.record ? summarizeSnapshot(result.record.snapshot) : null
    });
    return result;
  }

  public deleteSlot(slotId: number): void {
    this.assertSlotId(slotId);
    const envelope = this.readEditableEnvelope();
    envelope.slots[slotId - 1] = null;
    this.writeEnvelope(envelope);
    debugLogger.log('save', 'deleteSlot', { key: this.saveDataKey, slotId });
  }

  public clearSaveData(): void {
    this.storage.removeItem(this.saveDataKey);
    debugLogger.log('save', 'clearSaveData', { key: this.saveDataKey });
  }

  public saveSettings(settings: GameSettings): void {
    const envelope: SettingsEnvelope = {
      version: SETTINGS_VERSION,
      settings: normalizeSettings(settings, this.config)
    };
    this.storage.setItem(this.settingsKey, JSON.stringify(envelope));
    debugLogger.log('save', 'saveSettings', {
      key: this.settingsKey,
      settings: envelope.settings
    });
  }

  public loadSettings(): GameSettings | null {
    const rawValue = this.storage.getItem(this.settingsKey);
    if (!rawValue) {
      debugLogger.log('save', 'loadSettings empty', { key: this.settingsKey });
      return null;
    }

    try {
      const parsed = JSON.parse(rawValue) as unknown;
      if (!isRecord(parsed) || parsed.version !== SETTINGS_VERSION || !isRecord(parsed.settings)) {
        debugLogger.log('save', 'loadSettings invalid envelope', { key: this.settingsKey });
        return null;
      }

      const settings = normalizeSettings(parsed.settings, this.config);
      debugLogger.log('save', 'loadSettings', { key: this.settingsKey, settings });
      return settings;
    } catch {
      debugLogger.log('save', 'loadSettings parse failed', { key: this.settingsKey });
      return null;
    }
  }

  public deleteSettings(): void {
    this.storage.removeItem(this.settingsKey);
    debugLogger.log('save', 'deleteSettings', { key: this.settingsKey });
  }

  private readEnvelope():
    | { status: 'valid' | 'versionMismatch'; envelope: SaveEnvelope; migrated: boolean }
    | { status: 'corrupt'; reason: string } {
    const rawValue = this.storage.getItem(this.saveDataKey);
    if (!rawValue) {
      return { status: 'valid', envelope: createEmptyEnvelope(this.slotCount), migrated: false };
    }

    try {
      const parsed = JSON.parse(rawValue) as unknown;
      if (!isRecord(parsed) || !Array.isArray(parsed.slots)) {
        debugLogger.log('save', 'readEnvelope invalid shape', { key: this.saveDataKey });
        return { status: 'corrupt', reason: 'Save data is not a valid envelope.' };
      }

      if (parsed.version === SAVE_DATA_VERSION || parsed.version === LEGACY_SAVE_DATA_VERSION) {
        return {
          status: 'valid',
          envelope: {
            version: SAVE_DATA_VERSION,
            slots: resizeSlotArray(parsed.slots, this.slotCount)
          },
          migrated: parsed.version === LEGACY_SAVE_DATA_VERSION
        };
      }

      return {
        status: 'versionMismatch',
        envelope: {
          version: SAVE_DATA_VERSION,
          slots: resizeSlotArray(parsed.slots, this.slotCount)
        },
        migrated: false
      };
    } catch (error) {
      debugLogger.log('save', 'readEnvelope parse failed', {
        key: this.saveDataKey,
        reason: error instanceof Error ? error.message : String(error)
      });
      return {
        status: 'corrupt',
        reason: error instanceof Error ? error.message : String(error)
      };
    }
  }

  private readEditableEnvelope(): SaveEnvelope {
    const envelopeResult = this.readEnvelope();
    if (envelopeResult.status !== 'valid') {
      return createEmptyEnvelope(this.slotCount);
    }

    return envelopeResult.envelope;
  }

  private persistMigratedEnvelope(
    envelopeResult:
      | { status: 'valid' | 'versionMismatch'; envelope: SaveEnvelope; migrated: boolean }
      | { status: 'corrupt'; reason: string }
  ): void {
    if (
      'migrated' in envelopeResult &&
      envelopeResult.status === 'valid' &&
      envelopeResult.migrated
    ) {
      this.writeEnvelope(envelopeResult.envelope);
      debugLogger.log('save', 'persist migrated envelope', {
        key: this.saveDataKey,
        version: SAVE_DATA_VERSION
      });
    }
  }

  private writeEnvelope(envelope: SaveEnvelope): void {
    this.storage.setItem(
      this.saveDataKey,
      JSON.stringify({
        version: SAVE_DATA_VERSION,
        slots: normalizeSlotArray(envelope.slots, this.slotCount, this.config)
      })
    );
  }

  private assertSlotId(slotId: number): void {
    if (!Number.isInteger(slotId) || slotId < 1 || slotId > this.slotCount) {
      throw new RangeError(`Save slot ID must be between 1 and ${this.slotCount}.`);
    }
  }

  private createSlotRange(): number[] {
    return Array.from({ length: this.slotCount }, (_, index) => index + 1);
  }
}

function createEmptyEnvelope(slotCount: number): SaveEnvelope {
  return {
    version: SAVE_DATA_VERSION,
    slots: Array.from({ length: slotCount }, () => null)
  };
}

function summarizeSlot(
  slotId: number,
  slot: unknown,
  envelopeStatus: 'valid' | 'versionMismatch',
  config: GameConfig
): SaveSlotSummary {
  const result = parseSlot(slotId, slot, envelopeStatus, config);
  return {
    slotId,
    status: result.status,
    savedAt: result.record?.savedAt ?? null,
    title: result.record?.title ?? null,
    reason: result.reason
  };
}

function parseSlot(
  slotId: number,
  slot: unknown,
  envelopeStatus: 'valid' | 'versionMismatch',
  config: GameConfig
): SaveSlotLoadResult {
  if (slot === null || slot === undefined) {
    return { slotId, status: 'empty', record: null };
  }

  if (envelopeStatus === 'versionMismatch') {
    return {
      slotId,
      status: 'versionMismatch',
      record: null,
      reason: 'Save data version does not match.'
    };
  }

  if (
    isRecord(slot) &&
    typeof slot.version === 'number' &&
    slot.version !== SAVE_SLOT_VERSION &&
    slot.version !== LEGACY_SAVE_SLOT_VERSION
  ) {
    return {
      slotId,
      status: 'versionMismatch',
      record: null,
      reason: 'Save slot version does not match.'
    };
  }

  const record = parseRecord(slotId, slot, config);
  if (!record) {
    return {
      slotId,
      status: 'corrupt',
      record: null,
      reason: 'Save slot is not valid.'
    };
  }

  return { slotId, status: 'valid', record };
}

function parseRecord(slotId: number, value: unknown, config: GameConfig): SaveSlotRecord | null {
  if (
    !isRecord(value) ||
    (value.version !== SAVE_SLOT_VERSION && value.version !== LEGACY_SAVE_SLOT_VERSION) ||
    value.slotId !== slotId
  ) {
    return null;
  }

  if (typeof value.savedAt !== 'string' || typeof value.title !== 'string') {
    return null;
  }

  if (!isRecord(value.snapshot)) {
    return null;
  }

  return {
    version: SAVE_SLOT_VERSION,
    slotId,
    savedAt: value.savedAt,
    title: value.title,
    snapshot: normalizeSnapshot(value.snapshot, config)
  };
}

function normalizeSlotArray(
  slots: unknown[],
  slotCount: number,
  config: GameConfig
): Array<SaveSlotRecord | null> {
  return Array.from({ length: slotCount }, (_, index) => {
    const slotId = index + 1;
    const slot = slots[index];
    return parseRecord(slotId, slot, config) ?? null;
  });
}

function resizeSlotArray(slots: unknown[], slotCount: number): unknown[] {
  return Array.from({ length: slotCount }, (_, index) => slots[index] ?? null);
}

function normalizeSnapshot(
  snapshot: GameStateSnapshot | GameStateHydration,
  config: GameConfig
): GameStateSnapshot {
  return new GameState(config, snapshot).getSnapshot();
}

function normalizeSettings(settings: unknown, config: GameConfig): GameSettings {
  return new GameState(config, { settings: isRecord(settings) ? settings : {} }).getSettings();
}

function cloneRecord(record: SaveSlotRecord, config: GameConfig): SaveSlotRecord {
  return {
    version: record.version,
    slotId: record.slotId,
    savedAt: record.savedAt,
    title: record.title,
    snapshot: normalizeSnapshot(record.snapshot, config)
  };
}

function summarizeSnapshot(snapshot: GameStateSnapshot): Record<string, unknown> {
  return {
    currentScenarioId: snapshot.progress.currentScenarioId,
    currentCaseId: snapshot.progress.currentCaseId,
    resumePoint: snapshot.progress.resumePoint,
    evidenceIds: snapshot.progress.evidenceIds,
    caseStatuses: snapshot.progress.caseStatuses,
    backlogCount: snapshot.progress.backlog.length,
    readLineCount: snapshot.progress.readLineIds.length
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getDefaultStorage(): StorageLike {
  try {
    return globalThis.localStorage ?? createMemoryStorage();
  } catch {
    return createMemoryStorage();
  }
}

function createMemoryStorage(): StorageLike {
  const values = new Map<string, string>();

  return {
    getItem: (key) => values.get(key) ?? null,
    setItem: (key, value) => values.set(key, value),
    removeItem: (key) => values.delete(key)
  };
}
